/* eslint-disable max-len */
// ============================================================
// 非同步打卡 enqueue client helper
// ------------------------------------------------------------
// 把「呼叫 POST punch-in-records/enqueue 之後怎麼分流」這段集中起來，給三處共用：
//   - 首頁點擊 handleClickPunch（前景）
//   - offlinePunchScheduler（情境 4：/enqueue 呼叫本身失敗後的 local queue 重送）
//   - firestorePunchWatcher（publish_failed 換 key 重打）
//
// 分流（callPunchQueue 回傳）：
//   accepted  → 202（含重複 idempotency key 的冪等重放，回既有 document_id），拿到 task 去做 Firestore 監聽
//   retryable → 已持久化請求的 5xx / timeout / network error，保留原 key 與時間繼續補送
//   fatal     → 其他 4xx。新打卡的 classifyQueueError 預設仍限制「失敗當下」在線才能進 queue
// ============================================================

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { v4 as uuidv4 } from 'uuid';
import {
	postPunchInRecordQueue,
	IPunchInQueueRequest,
	IPunchInQueueTask,
	IPunchInRecord,
	TimeClockPunchPayload,
	postTimeClockPunchQueue,
} from '@src/services/punchIn';

dayjs.extend(utc);

export type CallPunchQueueResult =
	| { kind: 'accepted'; task: IPunchInQueueTask }
	| { kind: 'retryable'; deferredReason: string }
	| { kind: 'fatal'; response?: { status?: number; data?: { raw?: string; error_type?: string } } };

// 打卡相關 API 的統一 timeout：卡太久寧可轉入 local queue 背景重送，也不讓使用者停在 loading
export const PUNCH_API_TIMEOUT_MS = 30 * 1000;
export const MACHINE_QUEUE_TIMEOUT_MS = 1000;

export const newIdempotencyKey = (): string => uuidv4();

// 把原始打卡 payload 補上 punch_datetime（與選填 deferred_reason），組成 /enqueue 的 request body。
// 注意：idempotency key 不在 body，走 X-Idempotency-Key header（見 idempotencyConfigs）。
export const buildQueuePayload = (
	payload: IPunchInRecord | TimeClockPunchPayload,
	punchedAtMs: number,
	deferredReason?: string,
): IPunchInQueueRequest | TimeClockPunchPayload => ({
	...payload,
	punch_datetime: new Date(punchedAtMs).toISOString(),
	...(deferredReason ? { deferred_reason: deferredReason } : {}),
});

const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

export const parseEnqueueCreatedAt = (createdAt?: string): number | null => {
	if (!createdAt || typeof createdAt !== 'string') return null;
	const trimmed = createdAt.trim();
	const parsed = HAS_TIMEZONE.test(trimmed) ? dayjs(trimmed) : dayjs.utc(trimmed);
	return parsed.isValid() ? parsed.valueOf() : null;
};

// 後端要求 idempotency key 以 header 帶入（X-Idempotency-Key,）。
// 走 api 層的 customHeader 機制（src/api/request.js）：它會把 customHeader 併入並保留 Authorization。
// 不要用 `headers`——那會被 request 的 ...remainingConfigs 整包覆蓋掉自動注入的 Authorization。
export const idempotencyConfigs = (
	idempotencyKey: string,
	base: Record<string, unknown> = {},
): Record<string, unknown> => ({
	...base,
	customHeader: {
		...(base.customHeader as Record<string, string> | undefined),
		'X-Idempotency-Key': idempotencyKey,
	},
});

// 把一次失敗歸類成 deferred_reason（給 BE 做初步歸因），上限 64 chars。
export const classifyDeferredReason = (err: unknown): string => {
	const e = err as { response?: { status?: number }; code?: string };
	const status = e?.response?.status;
	const effectiveType = (typeof navigator !== 'undefined' ?
		(navigator as { connection?: { effectiveType?: string } }).connection?.effectiveType :
		undefined);
	const withNet = (reason: string): string => (effectiveType ? `${reason}_${effectiveType}` : reason);
	let reason: string;
	if (typeof status === 'number' && status >= 500) reason = `http_${status}`;
	else if (e?.code === 'ECONNABORTED') reason = withNet('timeout');
	else reason = withNet('network_error');
	return reason.slice(0, 64);
};

// 新打卡不接受純離線入 queue；已持久化請求則不能因之後斷網而丟掉原紀錄。
// component onError 沿用 foreground 預設，scheduler / watcher 的補送使用 persisted。
export const classifyQueueError = (
	err: unknown,
	context: 'foreground' | 'persisted' = 'foreground',
): CallPunchQueueResult => {
	const response = (err as { response?: { status?: number; data?: unknown } })?.response;
	const status = response?.status;
	// 註：重複 idempotency key 實測是回 202 + 既有 document_id（走成功分支），BE 不回 409，故這裡不特別處理 ALREADY_EXISTS。
	const isOnline = typeof window === 'undefined' ? true : window.navigator.onLine;
	const isRetryable = (!response || (typeof status === 'number' && status >= 500)) &&
		(context === 'persisted' || isOnline);
	if (isRetryable) {
		return { kind: 'retryable', deferredReason: classifyDeferredReason(err) };
	}
	return { kind: 'fatal', response: { status, data: response?.data as { raw?: string } } };
};

// 直接呼叫 /enqueue 並回傳分流結果（背景用：scheduler / watcher）。
// 前景（component）走 redux epic 拿 isLoading，改用 classifyQueueError 自行分流，不走這支。
export async function callPunchQueue(
	payload: IPunchInQueueRequest | TimeClockPunchPayload,
	idempotencyKey: string,
	configs?: Record<string, unknown>,
	punchKind?: 'regular' | 'machine',
): Promise<CallPunchQueueResult> {
	try {
		const task = punchKind === 'machine' ?
			await postTimeClockPunchQueue(payload as TimeClockPunchPayload, idempotencyConfigs(idempotencyKey, configs)) :
			await (postPunchInRecordQueue(payload as IPunchInQueueRequest,
				idempotencyConfigs(idempotencyKey, configs)) as unknown as Promise<IPunchInQueueTask>);
		if (punchKind === 'machine' && !task?.document_id) {
			return { kind: 'retryable', deferredReason: 'invalid_enqueue_response' };
		}
		return { kind: 'accepted', task };
	} catch (err) {
		return classifyQueueError(err, 'persisted');
	}
}
